import { Inject } from '@odinjs/odin';
import { FieldAPI, FormAPI, InputAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnCancel, OnChange, OnMount, OnSubmit, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService, Validator } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import moment from 'moment';
import { FragmentNames, MedicationSuspendAliases } from '../../../../codegen';

import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../constants/routes';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../utils/parameters';
import { TimeUtils } from '../../../../utils/time-utils';
import BaseController from '../../../BaseController';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { MedicationViewerParams } from '../medication-viewer/medication-viewer-params';
import { MedicineAndSolutionsParams } from '../medicine-and-solutions/medicine-and-solutions-params';
import { PrescriptionType } from '../../services/timeline-service';
import { YesNoType } from '../../../../utils/enum-utils';

import './medication-suspend-controller.css'

interface FormInterface {
  currentTime: string;
  description: string;
  justification: string;
  reasonType: {
    reasonDescription: string;
    sequenceId: number;
  }
  startDate: string;
}

interface AbortErrorInterface {
  body?: {
    errors?: string[]
  }
}

interface DateState {
  currentTime: moment.Moment,
  startDate?: moment.Moment
}

const requiredValidator = [
  {
    name: 'required'
  }
] as Validator[];

@Controller({ component: MedicationSuspendAliases.PANEL_SUSPEND_MEDICATION, fragment: FragmentNames.MEDICATION_SUSPEND })
class MedicationSuspendController extends BaseController {

  @Inject(MedicationSuspendAliases.FORM_MEDICATION_SUSPEND)
  private formApi!: FormAPI;

  @Inject(MedicationSuspendAliases.ADMINISTRATION_ROUTE_TYPE_FIELD)
  private fieldAdministrationRouteTypeApi!: FieldAPI;

  @OnMount(MedicationSuspendAliases.PANEL_SUSPEND_MEDICATION)
  onLayoutMount(): void {
    const {
      patientId,
      encounterId,
    } = this.historyService.getCurrentRouteParams();

    this.context.set({
      encounterId,
      formMounted: false,
      justificationFieldMounted: false,
      patientId,
      reasonFieldMounted: false,
    });

    InformationBarUtils.collapsedBar = true;
  }

  @OnMount(MedicationSuspendAliases.FORM_MEDICATION_SUSPEND)
  onFormMount(): void {
    const formRecord = this.formApi.getRecord();
    const dateState = this.getDateState();

    dateState.currentTime = moment();

    formRecord.description = MedicineAndSolutionsParams.params.dsMaterialId;
    formRecord.administrationRouteDescription = MedicineAndSolutionsParams.params.ieViaApplicationDescription;

    const startDate = MedicineAndSolutionsParams.params.startId;
    if(startDate) {
      dateState.startDate = moment(startDate);
      formRecord.startDate = dateState.startDate.format('L LTS');
    }

    this.setDateState(dateState);
    this.formApi.setRecord(formRecord);

    this.context.set({ formMounted: true });
  }

  getDateState(): DateState {
    const dateState = this.context.get('dateState') as DateState;
    return dateState || {};
  }

  setDateState(newState: DateState): void {
    this.context.set({dateState: newState});
  }

  @OnMount(MedicationSuspendAliases.DT_CURRENT_TIME_INPUT)
  onMountRecordDate(event: MountEvent): void {
    TimeUtils.setDateToInput(event.target as InputAPI);
  }

  @OnMount(MedicationSuspendAliases.ADMINISTRATION_ROUTE_TYPE_FIELD)
  onMountSuspendAdministrationRout(): void {
    this.fieldAdministrationRouteTypeApi.setVisible(!this.isProcedureAssociatedItem());
  }

  @OnMount(MedicationSuspendAliases.FIELD_REASON)
  onFieldReasonMount(): void {
    this.context.set({ reasonFieldMounted: true });
  }

  @OnValidate(MedicationSuspendAliases.FIELD_REASON)
  handleReasonFieldValidation({ target }: EventData<FieldAPI>): void {
    const requireReasonToSuspend = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.REQUIRE_REASON_TO_SUSPEND);
    const hasValidation = requireReasonToSuspend === YesNoType.YES || requireReasonToSuspend === 'A';
    if(hasValidation) {
      target.setValidators(requiredValidator);
    }
  }

  @OnMount(MedicationSuspendAliases.FIELD_TEXT_AREA_JUSTIFICATION)
  handleJustificationFieldValidation({ target }: EventData<FieldAPI>): void {
    const requireJustificationToSuspend = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.REQUIRE_JUSTIFICATION_ON_SUSPEND_ITEM);
    if(requireJustificationToSuspend === YesNoType.YES) {
      target.setValidators([
        {
            name: 'medication-suspend-field-justification-validator',
            options: {}
        }
        ,{ name: 'required' }
    ])
    }
  }

  @OnChange(MedicationSuspendAliases.DT_CURRENT_TIME_INPUT)
  onCurrentTimeInputChange(evt: ChangeEvent<InputAPI>): void {
    const dateState = this.getDateState();
    dateState.currentTime = moment(String(evt.value));
    this.setDateState(dateState);
  }

  @OnMount(MedicationSuspendAliases.DT_CURRENT_TIME)
  handleDateCurrentTimeMount(event: MountEvent): void {
    const fieldApi = event.target as FieldAPI;
    if (this.isProcedureAssociatedItem()) {
      fieldApi.setValidators(requiredValidator);
    }
  }

  @OnCancel(MedicationSuspendAliases.FORM_MEDICATION_SUSPEND)
  onFormCancel(): void {
    applicationContext.popNavigationItem();
  }

  @OnSubmit(MedicationSuspendAliases.FORM_MEDICATION_SUSPEND)
  onFormSubmit(): void {
    this.formApi.setLoading(true);
    const formRecord = this.formApi.getRecord() as unknown as FormInterface;
    const dateState = this.getDateState();

    const requestBody: Record<string, Value> = {
      alterationDate: dateState.currentTime?.format(ISO_FULL_DATE_FORMAT),
      encounterNumber: MedicineAndSolutionsParams.params.encounterId,
      eventDate: dateState.startDate?.format(ISO_FULL_DATE_FORMAT),
      hourSequence: MedicineAndSolutionsParams.params.hourSequence,
      itemCode: MedicineAndSolutionsParams.params.cdMaterialId,
      itemSequence: MedicineAndSolutionsParams.params.nrSequenceId,
      itemType: MedicineAndSolutionsParams.params.ieTypeItem,
      justifyDescription: formRecord.justification || '',
      prescriptionNumber: MedicationViewerParams.adminSchedule.prescriptionNumber,
      reasonSuspendSequence: formRecord.reasonType?.sequenceId,
    }

    this.tryToSuspendMedication(requestBody);
  }

  async tryToSuspendMedication(requestBody: Record<string, Value>): Promise<void> {
    const {
      patientId,
      encounterId
    } = this.historyService.getCurrentRouteParams();

    try {
      interface SuspendResponseBody {
        messageDescription: string;
      }

      const response = await HttpService.post(
        Endpoint.ADEP_SUSPEND_MEDICATION,
        {}, { body: requestBody }
      );

      const responseBody = response.body as SuspendResponseBody;

      if(responseBody.messageDescription) {
        this.showToast(responseBody.messageDescription, ToastLevel.WARNING);
      } else {
        this.showToast('label.medication-suspend.suspend-success', ToastLevel.SUCCESS);
      }
    } catch (error) {
      this.tryToShowAbortMessages(error as AbortErrorInterface);
    } finally {
      applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
        encounterId,
        patientId,
      });
      applicationContext.clearNavigationStack();
    }
  }

  tryToShowAbortMessages(abortError: AbortErrorInterface): void {
    if(abortError.body && abortError.body.errors) {
      abortError.body.errors.forEach(errorMessage => this.showToast(errorMessage, ToastLevel.ERROR));
    }
  }

  showToast(message: string, toastLevel: ToastLevel): void {
    const toastId = 'suspend-medication-toast';
    ToastService.close(toastId);
    ToastService.open({
      dismissible: true,
      id: toastId,
      level: toastLevel,
      message: MessageService.translate(message),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
    });
  }

  private isProcedureAssociatedItem(): boolean {
    const itemType = MedicineAndSolutionsParams.params.ieTypeItem;
    return itemType === PrescriptionType.PROCEDURES_ASSOCIATED_ITEMS
  }
}

export default MedicationSuspendController;
